import React, { useEffect, useState } from "react";
import { useScannerContext } from "../scanners/ScannerContext";
import { QualityCheckDto, QualityCheckAction, BarcodeDataType, ScanActionApiResponseNew } from "../scanners/types";
import { apiHost, apiKey, showManualQrCodeInput } from "@/constants";
import { appLog, barcodeDataTypeFromQrCode, hasQRcodeNumberPattern } from "../scanners/helpers";
import { ProductCard } from "@/components/ScannerActions/ProductCard";
import { BagCard } from "@/components/ScannerActions/BagCard";
import QrCodeInputField from "../scanners/QrCodeInputField";
import { OrderBag, OrderProduct } from "@/types";

const messages = {
  scan_bag_qrcode_message: "Scan eerst een zak barcode",
  invalid_qrcode_format_message: "Geen geldig formaat voor QR code van zak of product",
  invalid_qrcode_format_log: "[SCANNER_DATA] not a QRcode Number Pattern",
};

export const Scan: React.FC = () => {
  const { scannedData, setScannedData, enableScanner, clearScannedData } = useScannerContext();
  const [bagDataString, setBagDataString] = useState<string | null>(null);
  const [productDataString, setProductDataString] = useState<string | null>(null);
  const [orderBag, setOrderBag] = useState<Partial<OrderBag> | null>(null);
  const [orderProduct, setOrderProduct] = useState<Partial<OrderProduct> | null>(null);
  const [qualityCheckResponse, setQualityCheckResponse] = useState<ScanActionApiResponseNew | null>(null);
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    return () => {
      clearScannedData();
    };
  }, [clearScannedData]);

  useEffect(() => {
    if (scannedData) {
      if (hasQRcodeNumberPattern(scannedData)) {
        // Get the entity the barcode belongs to (product, bag, bin)
        const dataLabelType = barcodeDataTypeFromQrCode(scannedData);
        // Bag scan
        if (dataLabelType === BarcodeDataType.ORDER_BAG) {
          setBagDataString(scannedData);
          setProductDataString(null);
          // Product scan
        } else if (dataLabelType === BarcodeDataType.ORDER_PRODUCT) {
          if (bagDataString) {
            setProductDataString(scannedData);
          } else {
            setMessage(messages.scan_bag_qrcode_message);
          }
        } else {
          setMessage(messages.invalid_qrcode_format_message);
          appLog(`[KWC-BAGS] ${messages.invalid_qrcode_format_log}`, true, "info", { datalabel: scannedData });
        }
      } else {
        setMessage(messages.invalid_qrcode_format_message);
        appLog(`[KWC-BAGS] ${messages.invalid_qrcode_format_log}`, true, "info", { datalabel: scannedData });
      }
    }
  }, [scannedData, bagDataString]);

  const fetchApiData = async (bagDataString: string | null, productDataString: string | null) => {
    try {
      const data: QualityCheckDto = {
        action: QualityCheckAction.BAG_PRODUCTS,
        dataStrings: [],
      };
      // Add qr-code dataStrings to the request body
      if (bagDataString) {
        data.dataStrings.push({ type: BarcodeDataType.ORDER_BAG, dataString: bagDataString });
        if (productDataString) {
          data.dataStrings.push({ type: BarcodeDataType.ORDER_PRODUCT, dataString: productDataString });
        }
      } else {
        return;
      }

      const response = await fetch(`${apiHost}/order-quality-check`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
        },
        body: JSON.stringify(data),
      });
      const responseData: ScanActionApiResponseNew = await response.json();
      setQualityCheckResponse(responseData || null);
      if (responseData.orderBag) {
        setOrderBag(responseData.orderBag);
      }
      if (responseData.orderProduct) {
        setOrderProduct(responseData.orderProduct);
      }
    } catch (err) {
      appLog((err as Error).message, true, "error", err as Error);
      setOrderBag(null);
      setOrderProduct(null);
    }
  };

  useEffect(() => {
    setMessage("");
    if (bagDataString) {
      fetchApiData(bagDataString, productDataString);
    }
  }, [bagDataString, productDataString]);

  useEffect(() => {
    void enableScanner(true);
  }, [enableScanner]);

  return (
    <>
      <BagCard data={qualityCheckResponse} orderBag={orderBag} showScanMessage={true} />
      <ProductCard data={qualityCheckResponse} orderProduct={orderProduct} />
      {showManualQrCodeInput && (
        <QrCodeInputField
          onChange={(value: string) => {
            setScannedData(value);
          }}
        />
      )}
      {message && <div>{message}</div>}
    </>
  );
};

export default Scan;
